// socketService - WebSocket client for real-time card synchronization
// Implements US-2.1 and US-6.1: Real-time board updates

import { io, Socket } from 'socket.io-client';

// IMPORTANT: Socket.IO URL must NOT include /api path (that would be interpreted as a namespace)
// Empty string = connect to same origin (for single-domain production setup)
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || '';

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting' | 'error';

class SocketService {
  private socket: Socket | null = null;
  private roomId: string | null = null;
  private sessionId: string | null = null;
  private facilitatorId: string | null = null;
  private connectionStatus: ConnectionStatus = 'disconnected';
  private statusCallbacks: Array<(status: ConnectionStatus) => void> = [];

  /**
   * CONNECT to WebSocket server
   */
  connect(): void {
    if (this.socket?.connected) {
      console.log('Socket already connected');
      return;
    }

    // REUSE existing socket if it exists (don't disconnect on React remount)
    if (this.socket) {
      console.log('Reusing existing socket connection');
      this.socket.connect();
      return;
    }

    // CREATE socket connection with auto-reconnect
    this.socket = io(SOCKET_URL, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      autoConnect: true,
      withCredentials: true,
    });

    // HANDLE connection events
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.updateConnectionStatus('connected');

      // REJOIN room if we were in one before disconnect
      if (this.roomId && this.sessionId) {
        this.socket?.emit('room:join', {
          roomId: this.roomId,
          sessionId: this.sessionId,
          facilitatorId: this.facilitatorId
        });
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.updateConnectionStatus('disconnected');
    });

    this.socket.on('reconnect_attempt', () => {
      console.log('Attempting to reconnect...');
      this.updateConnectionStatus('reconnecting');
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Reconnection failed after max attempts');
      this.updateConnectionStatus('error');
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.updateConnectionStatus('error');
    });

    this.socket.on('error', (data: { message: string }) => {
      console.error('Socket error:', data.message);
    });
  }

  /**
   * UPDATE connection status and notify listeners
   */
  private updateConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status;
    this.statusCallbacks.forEach((callback) => callback(status));
  }

  /**
   * GET current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * LISTEN for connection status changes
   */
  onConnectionStatusChange(callback: (status: ConnectionStatus) => void): void {
    this.statusCallbacks.push(callback);
  }

  /**
   * REMOVE connection status listener
   */
  offConnectionStatusChange(callback: (status: ConnectionStatus) => void): void {
    this.statusCallbacks = this.statusCallbacks.filter((cb) => cb !== callback);
  }

  /**
   * DISCONNECT from WebSocket server (but keep socket instance for reconnection)
   */
  disconnect(): void {
    if (this.socket?.connected) {
      this.socket.disconnect();
      // DON'T nullify socket - keep it for reconnection on React remount
    }
  }

  /**
   * JOIN a room for real-time updates
   */
  joinRoom(roomId: string, sessionId: string, facilitatorId?: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.roomId = roomId;
    this.sessionId = sessionId;
    this.facilitatorId = facilitatorId || null;

    // EMIT room join event
    this.socket.emit('room:join', { roomId, sessionId, facilitatorId });

    console.log(`Joined room: ${roomId} (facilitator: ${!!facilitatorId})`);
  }

  /**
   * LEAVE current room
   */
  leaveRoom(): void {
    if (!this.socket || !this.roomId) {
      return;
    }

    // EMIT room leave event
    this.socket.emit('room:leave', { roomId: this.roomId });

    console.log(`Left room: ${this.roomId}`);
    this.roomId = null;
  }

  /**
   * CREATE new card via WebSocket
   */
  createCard(columnKey: string, text: string, authorName?: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    // EMIT card creation event
    this.socket.emit('card:create', {
      roomId: this.roomId,
      columnKey,
      text,
      authorName,
    });
  }

  /**
   * UPDATE card via WebSocket
   */
  updateCard(cardId: string, text: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    // EMIT card update event
    this.socket.emit('card:update', {
      cardId,
      text,
    });
  }

  /**
   * DELETE card via WebSocket
   */
  deleteCard(cardId: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    // EMIT card delete event
    this.socket.emit('card:delete', { cardId });
  }

  /**
   * TOGGLE card expand/collapse via WebSocket (US-2.4)
   */
  toggleCardExpand(cardId: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    // EMIT card toggle expand event
    this.socket.emit('card:toggle_expand', { cardId });
  }

  /**
   * TOGGLE card discussed status via WebSocket (US-3.3)
   * Only facilitator can mark cards as discussed during facilitation phase
   */
  toggleCardDiscussed(cardId: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    // EMIT card toggle discuss event
    this.socket.emit('card:toggle_discuss', { cardId });
  }

  /**
   * LISTEN for card created events
   */
  onCardCreated(callback: (data: { card: any }) => void): void {
    if (!this.socket) return;
    this.socket.on('card:created', callback);
  }

  /**
   * LISTEN for card updated events
   */
  onCardUpdated(callback: (data: { card: any }) => void): void {
    if (!this.socket) return;
    this.socket.on('card:updated', callback);
  }

  /**
   * LISTEN for card deleted events
   */
  onCardDeleted(callback: (data: { cardId: string }) => void): void {
    if (!this.socket) return;
    this.socket.on('card:deleted', callback);
  }

  /**
   * LISTEN for participant count updates
   */
  onParticipantCount(callback: (data: { count: number }) => void): void {
    if (!this.socket) return;
    this.socket.on('room:participant_count', callback);
  }

  /**
   * START facilitation phase (facilitator only)
   */
  startFacilitation(roomId: string, facilitatorId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    // EMIT phase transition event
    this.socket.emit('room:start_facilitation', { roomId, facilitatorId });
    console.log(`Starting facilitation for room: ${roomId}`);
  }

  /**
   * LISTEN for phase change events
   */
  onPhaseChanged(callback: (data: { phase: string; room: any }) => void): void {
    if (!this.socket) return;
    this.socket.on('room:phase_changed', callback);
  }

  /**
   * CLOSE room and destroy all data (facilitator only, US-1.3)
   */
  closeRoom(roomId: string, facilitatorId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    // EMIT room close event
    this.socket.emit('room:close', { roomId, facilitatorId });
    console.log(`Closing room: ${roomId}`);
  }

  /**
   * LISTEN for room closed events (US-1.3)
   */
  onRoomClosed(callback: (data: { id: string; code: string; name: string; wasExported: boolean; closedAt: Date }) => void): void {
    if (!this.socket) return;
    this.socket.on('room:closed', callback);
  }

  /**
   * REMOVE event listeners
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) return;
    this.socket.off(event, callback);
  }

  /**
   * CHECK if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // ========== GROUP OPERATIONS (US-2.2) ==========

  /**
   * CREATE group from multiple cards
   */
  createGroup(column: string, cardIds: string[], label?: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    this.socket.emit('group:create', {
      roomId: this.roomId,
      column,
      cardIds,
      label,
    });
  }

  /**
   * UPDATE group label
   */
  updateGroupLabel(groupId: string, label: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('group:update_label', {
      groupId,
      label,
    });
  }

  /**
   * ADD card to existing group
   */
  addCardToGroup(groupId: string, cardId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('group:add_card', {
      groupId,
      cardId,
    });
  }

  /**
   * REMOVE card from group (ungroup single card)
   */
  removeCardFromGroup(cardId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('group:remove_card', {
      cardId,
    });
  }

  /**
   * DELETE entire group
   */
  deleteGroup(groupId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('group:delete', {
      groupId,
    });
  }

  /**
   * UNGROUP all cards in room
   */
  ungroupAll(roomId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('group:ungroup_all', {
      roomId,
    });
  }

  /**
   * LISTEN for group created events
   */
  onGroupCreated(callback: (data: { group: any }) => void): void {
    if (!this.socket) return;
    this.socket.on('group:created', callback);
  }

  /**
   * LISTEN for group updated events
   */
  onGroupUpdated(callback: (data: { group: any }) => void): void {
    if (!this.socket) return;
    this.socket.on('group:updated', callback);
  }

  /**
   * LISTEN for group deleted events
   */
  onGroupDeleted(callback: (data: { groupId: string }) => void): void {
    if (!this.socket) return;
    this.socket.on('group:deleted', callback);
  }

  /**
   * LISTEN for ungroup all events
   */
  onUngroupedAll(callback: (data: { success: boolean }) => void): void {
    if (!this.socket) return;
    this.socket.on('group:ungrouped_all', callback);
  }

  // ========== VOTING OPERATIONS (US-3.2) ==========

  /**
   * START voting (facilitator only)
   */
  startVoting(roomId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('voting:start', { roomId });
    console.log(`Starting voting for room: ${roomId}`);
  }

  /**
   * END voting (facilitator only)
   */
  endVoting(roomId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('voting:end', { roomId });
    console.log(`Ending voting for room: ${roomId}`);
  }

  /**
   * RESET voting (facilitator only)
   */
  resetVoting(roomId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('voting:reset', { roomId });
    console.log(`Resetting votes for room: ${roomId}`);
  }

  /**
   * CAST vote on a card
   */
  castVote(cardId: string, dots: number): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    this.socket.emit('voting:cast', {
      roomId: this.roomId,
      cardId,
      dots,
    });
  }

  /**
   * REMOVE vote from a card
   */
  removeVote(cardId: string, dots: number): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    this.socket.emit('voting:remove', {
      roomId: this.roomId,
      cardId,
      dots,
    });
  }

  /**
   * LISTEN for voting started events
   */
  onVotingStarted(callback: (data: { roomId: string }) => void): void {
    if (!this.socket) return;
    this.socket.on('voting:started', callback);
  }

  /**
   * LISTEN for voting ended events
   */
  onVotingEnded(callback: (data: { roomId: string }) => void): void {
    if (!this.socket) return;
    this.socket.on('voting:ended', callback);
  }

  /**
   * LISTEN for voting reset events
   */
  onVotingReset(callback: (data: { roomId: string }) => void): void {
    if (!this.socket) return;
    this.socket.on('voting:reset', callback);
  }

  /**
   * LISTEN for vote updates (real-time vote count changes)
   */
  onVotingUpdated(callback: (data: { cardId: string; voteCount: number }) => void): void {
    if (!this.socket) return;
    this.socket.on('voting:updated', callback);
  }

  // ========== ACTION ITEM OPERATIONS (US-2.3) ==========

  /**
   * CREATE action item (facilitator only)
   */
  createActionItem(text: string, owner?: string, dueDate?: string): void {
    if (!this.socket || !this.roomId) {
      throw new Error('Not connected to room');
    }

    // DEBUG: Log what we're about to send
    console.log('🚀 Emitting action:create:', { roomId: this.roomId, text, owner, dueDate });

    this.socket.emit('action:create', {
      roomId: this.roomId,
      text,
      owner,
      dueDate,
    });

    console.log(`Creating action item in room: ${this.roomId}`);
  }

  /**
   * UPDATE action item metadata (facilitator only)
   */
  updateActionItem(cardId: string, owner?: string, dueDate?: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('action:update', {
      cardId,
      owner,
      dueDate,
    });

    console.log(`Updating action item: ${cardId}`);
  }

  /**
   * MOVE card to actions column (facilitator only)
   */
  moveCardToActions(cardId: string, owner?: string, dueDate?: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('action:move_to_actions', {
      cardId,
      owner,
      dueDate,
    });

    console.log(`Moving card to actions: ${cardId}`);
  }

  /**
   * MOVE card between columns (drag-and-drop, facilitator only)
   */
  moveCard(cardId: string, targetColumn: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('card:move', {
      cardId,
      targetColumn,
    });

    console.log(`Moving card ${cardId} to column: ${targetColumn}`);
  }
}

// EXPORT singleton instance
export const socketService = new SocketService();
